import React from 'react'
import { AlertTriangle, Save, X, CreditCard } from 'lucide-react'
import { Button } from '../ui/Button'
import { cn } from '../../lib/utils'

interface ExitConfirmationDialogProps {
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
  hasUnsavedChanges?: boolean
}

export const ExitConfirmationDialog: React.FC<ExitConfirmationDialogProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  hasUnsavedChanges = true
}) => {
  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 animate-in fade-in duration-200"
        onClick={onCancel}
      />
      
      {/* Dialog */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md animate-in zoom-in-95 duration-200">
        <div className="bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                {hasUnsavedChanges ? 'Save Your Progress?' : 'Exit Setup?'}
              </h2>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="px-6 pb-4">
            <p className="text-gray-600">
              {hasUnsavedChanges 
                ? "Your configuration progress will be automatically saved. You can return anytime to complete the setup."
                : "You can return anytime to continue where you left off."
              }
            </p>
            
            {/* Payment reminder */}
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <CreditCard className="w-4 h-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <strong>No charges yet!</strong> Payment ($500 deposit) is only required after you complete all setup steps and choose your service plan.
                </div>
              </div>
            </div>
            
            {hasUnsavedChanges && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Save className="w-4 h-4 text-green-600 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <strong>Auto-save active:</strong> Your progress up to the previous step has been saved automatically.
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex items-center justify-end gap-3 p-6 pt-2">
            <Button
              variant="outline"
              onClick={onCancel}
              className="px-4 py-2"
            >
              Continue Setup
            </Button>
            <Button
              onClick={onConfirm}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white"
            >
              Save & Exit
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}